import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
} from "react";
import { getErrorMessage } from "@/core/api/errors";
import type { useFollowAiScriptExplanationChatTurn } from "@/payroll/ai-script/query";
import type { IAiScriptExplanationChatActiveTurn } from "@/payroll/ai-script/types";
import { buildAssistantErrorMessage } from "./chat-session-helpers";
import type { ChatMessage } from "./types";
import type { ThreadLiveStates } from "./use-thread-live-states";

export const useFollowActiveTurn = ({
  businessId,
  runId,
  selectedThreadId,
  activeTurn,
  followTurn,
  followedTurnKeysRef,
  selectedThreadIdRef,
  setMessages,
  liveStates: {
    setLocalRunningTurnsByThreadId,
    markThreadFollowing,
    appendLiveTextDelta,
    appendLiveProgressStep,
    finishThreadTurn,
    setPendingThreadResponse,
    clearLiveResponse,
  },
}: {
  businessId: string | null;
  runId: string;
  selectedThreadId: string | null;
  activeTurn: IAiScriptExplanationChatActiveTurn | null | undefined;
  followTurn: ReturnType<typeof useFollowAiScriptExplanationChatTurn>["mutate"];
  followedTurnKeysRef: MutableRefObject<Set<string>>;
  selectedThreadIdRef: MutableRefObject<string | null>;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  liveStates: ThreadLiveStates;
}) => {
  useEffect(() => {
    if (!businessId || !selectedThreadId || !activeTurn) return;

    const turnKey = `${selectedThreadId}:${activeTurn.turn_id}`;
    if (followedTurnKeysRef.current.has(turnKey)) return;

    followedTurnKeysRef.current.add(turnKey);
    setLocalRunningTurnsByThreadId((currentTurns) => ({
      ...currentTurns,
      [selectedThreadId]: activeTurn.turn_id,
    }));
    markThreadFollowing(selectedThreadId, true);

    followTurn(
      {
        runId,
        threadId: selectedThreadId,
        turnId: activeTurn.turn_id,
        onTextDelta: (delta) => {
          appendLiveTextDelta(selectedThreadId, delta);
        },
        onProgress: (label) => {
          appendLiveProgressStep(selectedThreadId, label);
        },
      },
      {
        onSuccess: ({ response, threadId }) => {
          finishThreadTurn(threadId);
          setPendingThreadResponse(threadId, response);
        },
        onError: (error: unknown) => {
          const message = getErrorMessage(error);
          followedTurnKeysRef.current.delete(turnKey);
          finishThreadTurn(selectedThreadId);
          clearLiveResponse(selectedThreadId);
          if (selectedThreadIdRef.current !== selectedThreadId) return;

          setMessages((currentMessages) => [
            ...currentMessages,
            buildAssistantErrorMessage(message),
          ]);
        },
      },
    );
  }, [
    businessId,
    appendLiveProgressStep,
    appendLiveTextDelta,
    clearLiveResponse,
    finishThreadTurn,
    followTurn,
    markThreadFollowing,
    runId,
    activeTurn,
    selectedThreadId,
    setPendingThreadResponse,
    // Stable (refs and state setters), listed because they arrive as arguments.
    followedTurnKeysRef,
    selectedThreadIdRef,
    setLocalRunningTurnsByThreadId,
    setMessages,
  ]);
};
